import { Injectable } from '@angular/core';

import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { PageName, NavigationService } from '@omni/services/navigation/navigation.service';
import { AccountOfflineService } from '@omni/services/account/account.offline.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { FeatureActionsService } from '@omni/services/feature-actions/feature-actions.service';
import { KitBookingFormService } from '@omni/services/kit-booking/kit-booking-form.service';
import { OpportunityManagementService } from '@omni/services/opportunity-management/opportunity-management.service';
import { ActivityCreationService, QuickCreateContext } from './activity-creation.service';
import { QuickActionStripService, QuickActionType } from './quick-action-strip.service';

/**
 * Everything a rep can start from the selected account — the account card's half of
 * ContactQuickActionsService, shared the same way between the details pane and the timeline
 * page.
 */
@Injectable({ providedIn: 'root' })
export class AccountQuickActionsService {

  constructor(
    private readonly accountService: AccountOfflineService,
    private readonly activityCreationService: ActivityCreationService,
    private readonly authService: AuthenticationService,
    private readonly featureActionService: FeatureActionsService,
    private readonly kitBookingFormService: KitBookingFormService,
    private readonly navService: NavigationService,
    private readonly opportunityService: OpportunityManagementService,
    private readonly strip: QuickActionStripService,
  ) { }

  /**
   * Everything a rep can create from this account, in the order New Activity lists it.
   * Nine types: the account tool turns off sampling, allocation orders, email, time off,
   * direct messaging, customer and medical inquiry, live meet and scientific plan tasks.
   */
  public types(): QuickActionType[] {
    const fa = (action: FeatureActionsMap) => this.authService.hasFeatureAction(action);
    return [
      {
        id: 'quick-meeting', labelKey: 'MEETING', icon: 'omni_quick_glance_meeting.svg',
        isEnabled: () => !(this.featureActionService.isShortCallLauncherEnabledInMobileDevice
          && fa(FeatureActionsMap.SHOR_CALL_LAUNCHER_DISABLE_APPOINTMENT_IN_NEW_ACTIVITY)),
        run: event => this.createMeeting(event),
        menuOptions: () => this.activityCreationService.meetingFormatOptions(),
        runWithOptionId: async id => {
          const format = this.activityCreationService.meetingFormats().find(format => format.indskr_activitytypeid === id);
          await this.activityCreationService.create('meeting', { meetingFormat: format, context: this.context() });
        },
        dictateWithOptionId: id => this.activityCreationService.dictateMeeting(id, this.context()).then(() => {}),
      },
      {
        id: 'phone-call', labelKey: 'NEW_ACTIVITY_PHONECALL', icon: 'omni_quick_glance_call.svg',
        isEnabled: () => fa(FeatureActionsMap.PHONECALL_ACTIVITY),
        run: () => this.activityCreationService.create('phoneCall', { context: this.context() }),
      },
      {
        id: 'account-visit', labelKey: 'ACCOUNT_VISIT', icon: 'account_visit.svg',
        isEnabled: () => fa(FeatureActionsMap.ACCOUNT_VISIT),
        // The visit endpoint is online only; accountVisitOfflineCheck refuses with its own toast.
        disabledReason: () => this.activityCreationService.offlineRefusal('accountVisit'),
        run: () => this.activityCreationService.create('accountVisit', { context: this.context() }),
      },
      this.strip.procedureLogType(() => this.context()),
      this.strip.followUpType('account', () => this.context()),
      {
        id: 'sales-order', labelKey: 'NEW_ACTIVITY_SALES_ORDER', icon: 'nav/sales-orders.svg',
        isEnabled: () => fa(FeatureActionsMap.ORDER_MANAGEMENT),
        run: () => this.activityCreationService.create('salesOrder', { context: this.context() }),
      },
      {
        id: 'store-check', labelKey: 'STORE_CHECK', icon: 'storecheck.svg',
        isEnabled: () => fa(FeatureActionsMap.STORE_CHECK),
        // createStoreCheck refuses offline with STORE_CHECK_OFFLINE.
        disabledReason: () => this.activityCreationService.offlineRefusal('storeCheck'),
        run: () => this.activityCreationService.create('storeCheck', { context: this.context() }),
      },
      {
        id: 'kit-booking', labelKey: 'KIT_BOOKING', icon: 'kit-booking.svg',
        isEnabled: () => fa(FeatureActionsMap.KIT_BOOKING),
        disabledReason: () => this.activityCreationService.offlineRefusal('kitBooking'),
        run: () => this.activityCreationService.runExclusive(() => this.kitBookingFormService.openCreateForm({
          layoverPushedFrom: 'AccountTool',
          layoverPassedData: this.accountService.selected,
          replacesNewActivity: false,
        })),
      },
      {
        id: 'opportunity', labelKey: 'OPPORTUNITY', icon: 'nav/opportunities.svg',
        isEnabled: () => fa(FeatureActionsMap.OPPORTUNITY_MANAGEMENT),
        run: () => this.activityCreationService.runExclusive(() => this.newOpportunity()),
      },
    ];
  }

  private context(): QuickCreateContext {
    return {
      account: this.accountService.selected,
      host: PageName.AccountPageComponent,
      restoreView: 'accountDetails',
    };
  }

  private async createMeeting(event?: Event): Promise<void> {
    if (this.activityCreationService.meetingAsksFirst() && event) {
      await this.strip.chooseOptionAndRun(event, this.types(), 'quick-meeting');
      return;
    }
    await this.activityCreationService.create('meeting', { context: this.context() });
  }

  private async newOpportunity(): Promise<void> {
    const account = this.accountService.getAccountById(this.accountService.selected?.id);
    await this.navService.pushChildNavPageWithPageTracking(
      PageName.NewOpportunityComponent,
      PageName.NewOpportunityComponent,
      PageName.AccountPageComponent,
      { from: PageName.AccountPageComponent },
    );
    this.navService.setChildNavRightPaneView(true);
    this.opportunityService.prefillNewOpportunity(account);
  }
}
